import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Graph, Thing, WithContext } from 'schema-dts';

type JsonLdValue<T extends Thing = Thing> = Graph | WithContext<T>;
type JsonLdEntry = {
  jsonLd: JsonLdValue;
  isStatic: boolean;
};

function isGraphEntry(entry: JsonLdValue): entry is Graph {
  return '@graph' in entry;
}

@Injectable({
  providedIn: 'root',
})
export class JsonLdService implements OnDestroy {

  private document = inject(DOCUMENT);

  private entries = new Map<string, JsonLdEntry>();
  private dynamicScriptElement?: HTMLScriptElement;
  private staticScriptElement?: HTMLScriptElement;

  public ngOnDestroy(): void {
    this.entries.clear();
    this.dynamicScriptElement?.remove();
    this.staticScriptElement?.remove();
  }

  public set<T extends Thing>(key: string, jsonLd: JsonLdValue<T>, isStatic = false): void {
    this.entries.set(key, { jsonLd, isStatic });
    this.render(isStatic);
  }

  public remove(key: string): void {
    const entry = this.entries.get(key);
    if (entry === undefined) {
      return;
    }

    this.entries.delete(key);
    this.render(entry.isStatic);
  }

  public register<T extends Thing>(key: string, jsonLd: JsonLdValue<T>, isStatic = false): () => void {
    this.set(key, jsonLd, isStatic);
    return () => this.remove(key);
  }

  private render(isStatic: boolean): void {
    const entries = Array.from(this.entries.values())
      .filter((entry) => entry.isStatic === isStatic)
      .map((entry) => entry.jsonLd);

    const scriptElement = this.getScriptElement(isStatic);
    if (entries.length === 0) {
      scriptElement?.remove();
      this.setScriptElement(isStatic, undefined);
      return;
    }

    const nextScriptElement = scriptElement ?? this.createScriptElement(isStatic);
    nextScriptElement.textContent = JSON.stringify(this.toGraphDocument(entries));

    if (nextScriptElement.isConnected === false) {
      this.document.head.appendChild(nextScriptElement);
    }

    this.setScriptElement(isStatic, nextScriptElement);
  }

  private toGraphDocument(entries: JsonLdValue[]): Graph {
    return {
      '@context': 'https://schema.org',
      '@graph': entries.flatMap((entry) => this.normalizeEntry(entry)),
    };
  }

  private normalizeEntry(entry: JsonLdValue): Thing[] {
    if (isGraphEntry(entry)) {
      return [...entry['@graph']];
    }

    const thing = { ...(entry as unknown as { [key: string]: unknown }) };
    delete thing['@context'];
    return [thing as unknown as Thing];
  }

  private createScriptElement(isStatic: boolean): HTMLScriptElement {
    const element = this.document.createElement('script');
    element.setAttribute('type', 'application/ld+json');
    if (isStatic) {
      element.setAttribute('static', '');
    }
    return element;
  }

  private getScriptElement(isStatic: boolean): HTMLScriptElement | undefined {
    return isStatic ? this.staticScriptElement : this.dynamicScriptElement;
  }

  private setScriptElement(isStatic: boolean, element: HTMLScriptElement | undefined): void {
    if (isStatic) {
      this.staticScriptElement = element;
      return;
    }

    this.dynamicScriptElement = element;
  }

}
